import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, Grid, Button, List, ListItem, ListItemText, Divider, Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import InfoIcon from '@mui/icons-material/Info';

function ResultsPanel({
  results, onDownload, onReset,
}) {
  const uniqueHashes = results.outputs.reduce((sum, o) => sum + o.hashCount, 0);

  const isMulti = results.outputs.length > 1;
  const perFile = results.perFile ?? [];
  const showPerFileBreakdown = perFile.length > 1;
  const showAggregate = !(showPerFileBreakdown && isMulti);
  // Only a 1:1 exportMode="separate" run has one output per source file — merged
  // output can't be attributed back to an individual source file's unique count.
  const perFileHasOwnOutput = results.outputs.length === perFile.length;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex', alignItems: 'center', mb: 2, gap: 1,
        }}
      >
        <CheckCircleOutlinedIcon color="success" />
        <Typography variant="h6">Hashing Complete</Typography>
      </Box>

      {showAggregate && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <Typography variant="body2" color="text.secondary">Total Rows</Typography>
            <Typography variant="h5">{results.totalRows}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2" color="text.secondary">Discarded</Typography>
            <Typography variant="h5">{results.discardedRows}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2" color="text.secondary">Unique Hashes</Typography>
            <Typography variant="h5">{uniqueHashes}</Typography>
          </Grid>
        </Grid>
      )}

      {showPerFileBreakdown && (
        <>
          <Box sx={{ display: 'flex', mb: 1 }}>
            <Typography sx={{ fontWeight: 'bold', mr: 1 }}>Per-File Breakdown</Typography>
            <Tooltip
              title="The per-file breakdown shows the total number of rows in the original file and discarded rows for each input file. The discarded count includes invalid rows as well as duplicates that were removed. If the run was a 1:1 separate export, it also shows the number of unique hashes generated for each file."
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: '1rem',
                  },
                },
              }}
            >
              <InfoIcon sx={{ fill: '#5e6262' }} />
            </Tooltip>
          </Box>
          <List dense sx={{ mb: 2 }}>
            {perFile.map((f, i) => {
              const uniqueForFile = perFileHasOwnOutput
                ? results.outputs[i].hashCount
                : null;
              const secondary = uniqueForFile === null
                ? `${f.totalRows} rows, ${f.discardedRows} discarded`
                : `${f.totalRows} rows, ${f.discardedRows} discarded, ${uniqueForFile} unique`;
              return (
                <ListItem key={f.filename} disableGutters>
                  <ListItemText primary={f.filename} secondary={secondary} />
                </ListItem>
              );
            })}
          </List>
        </>
      )}

      <Typography sx={{ fontWeight: 'bold' }}>Output Files</Typography>
      <List dense sx={{ mb: 2 }}>
        {results.outputs.map((output) => (
          <ListItem key={output.filename} disableGutters>
            <ListItemText primary={output.filename} />
          </ListItem>
        ))}
      </List>

      <Button
        variant="contained"
        size="large"
        startIcon={<DownloadIcon />}
        onClick={() => onDownload(results.outputs)}
        sx={{ mr: 2 }}
      >
        {isMulti ? 'Download All Hash Files' : 'Download Hash File'}
      </Button>

      <Divider sx={{ my: 3 }} />
      <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={onReset}>
        Start Over
      </Button>
    </Box>
  );
}

ResultsPanel.propTypes = {
  results: PropTypes.shape({
    outputs: PropTypes.arrayOf(PropTypes.shape({
      filename: PropTypes.string.isRequired,
      content: PropTypes
        .oneOfType([PropTypes.string, PropTypes.instanceOf(ArrayBuffer)]).isRequired,
      mimeType: PropTypes.string.isRequired,
      hashCount: PropTypes.number.isRequired,
    })).isRequired,
    totalRows: PropTypes.number.isRequired,
    discardedRows: PropTypes.number.isRequired,
    perFile: PropTypes.arrayOf(PropTypes.shape({
      filename: PropTypes.string.isRequired,
      totalRows: PropTypes.number.isRequired,
      discardedRows: PropTypes.number.isRequired,
    })),
  }).isRequired,
  onDownload: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};

export default ResultsPanel;
